import { useCallback, useState } from "react";

/**
 * Session-scope persistent state.
 *
 * Drop-in replacement for `useState` that survives component unmount /
 * remount within the same browser session, but does NOT survive a hard
 * refresh. Useful for UI state that should persist as the user
 * navigates between pages (range toggles, chip selections, axis
 * choices) without leaking across tabs or surviving a manual reload.
 *
 * Backed by a module-scope Map. All callers using the same `key` share
 * the same value, so use a unique key per piece of state (e.g.
 * "primaryParams.range", "primaryParams.selectedIds").
 */
const sessionStore = new Map<string, unknown>();

export function usePersistentState<T>(
  key: string,
  initial: T | (() => T),
): [T, (next: T | ((prev: T) => T)) => void] {
  const [value, setValue] = useState<T>(() => {
    if (sessionStore.has(key)) return sessionStore.get(key) as T;
    return typeof initial === "function" ? (initial as () => T)() : initial;
  });

  // Keep the setter referentially stable per `key` so consumers can put
  // it in effect dependency arrays without re-running on every render.
  const setPersistent = useCallback(
    (next: T | ((prev: T) => T)) => {
      setValue((prev) => {
        const resolved =
          typeof next === "function" ? (next as (p: T) => T)(prev) : next;
        sessionStore.set(key, resolved);
        return resolved;
      });
    },
    [key],
  );

  return [value, setPersistent];
}
